import { IAssignment } from '@medely/types';
import { isHolidayDifferential } from './jobUtils';
import { JobWithJobType } from '@mp/graphql';
import { ShiftRequestStatus } from '@mp/graphql/generated/graphql';
import { useRateRequest } from '@mp/components/jobs/rateRequest/useRateRequest';
import { useCurrentUser } from '@mp/hooks';

const MAX_VISIBLE_BADGES = 3;

export enum BADGE_TYPE {
  great_pay = 'great-pay',
  missing_requirements = 'missing-requirements',
  holiday = 'holiday',
  bonus = 'bonus',
  distance = 'distance',
  travel_distance = 'travel-distance',
  first_to_apply = 'first-to-apply',
  rate_request = 'rate-request',
}

export type Badge = {
  variant: BADGE_TYPE;
  value?: number;
  rate_request_status?: ShiftRequestStatus;
};

export interface IBadgedAssignment extends IAssignment {
  badges?: Badge[];
}

export interface IBadgedJob extends JobWithJobType {
  badges?: Badge[];
  pool_id?: number | null;
}

interface GetBadgedJobsProps {
  jobs: JobWithJobType[];
  currentUser: ReturnType<typeof useCurrentUser>;
  rateNegotiationFgEnabled: boolean;
}

const filterBadges = (badges: Array<Badge | false>): Badge[] =>
  badges.filter((badge): badge is Badge => !!badge).slice(0, MAX_VISIBLE_BADGES);

export const badgedJobs = ({
  jobs,
  currentUser,
  rateNegotiationFgEnabled,
}: GetBadgedJobsProps): IBadgedJob[] =>
  jobs.map((job) => {
    const { mostRecentRateRequest, shouldDisplayRateRequestBadge } = useRateRequest({
      job,
      isMarketplace: currentUser.proType === 'marketplace',
      isFgEnabled: rateNegotiationFgEnabled,
    });

    const hasMissingRequirements = !job.can_book_job && !!job.missing_condition_ids?.length;
    const isGreatPay = job.great_pay;
    const isHoliday = job.job_price_differentials?.some(isHolidayDifferential);
    const shouldDisplayDistance = !!job.distance_in_miles;

    const badges = filterBadges([
      hasMissingRequirements && { variant: BADGE_TYPE.missing_requirements },
      isGreatPay && { variant: BADGE_TYPE.great_pay },
      isHoliday && { variant: BADGE_TYPE.holiday },
      shouldDisplayDistance && { variant: BADGE_TYPE.distance, value: job.distance_in_miles },
      shouldDisplayRateRequestBadge &&
        !!mostRecentRateRequest && {
          variant: BADGE_TYPE.rate_request,
          rate_request_status: mostRecentRateRequest.status,
        },
    ]);

    return { ...job, badges };
  });

export const localBadgedAssignments = ({
  assignments,
}: {
  assignments: IAssignment[];
}): IBadgedAssignment[] =>
  assignments.map((assignment) => {
    const hasBonus = !!assignment?.one_time_bonus_burden_cents;
    const hasMissingRequirements = !assignment.can_apply;
    const isGreatPay = assignment.great_pay;
    const distance = !!assignment.distance_in_miles;

    const badges = filterBadges([
      hasBonus && { variant: BADGE_TYPE.bonus, value: assignment.one_time_bonus_burden_cents },
      hasMissingRequirements && { variant: BADGE_TYPE.missing_requirements },
      isGreatPay && { variant: BADGE_TYPE.great_pay },
      distance && { variant: BADGE_TYPE.distance, value: assignment.distance_in_miles },
    ]);

    return { ...assignment, badges };
  });

export const travelBadgedAssignments = ({
  assignments,
}: {
  assignments: IAssignment[];
}): IBadgedAssignment[] =>
  assignments.map((assignment) => {
    const hasBonus = !!assignment?.one_time_bonus_burden_cents;
    const hasMissingRequirements = !assignment.can_apply;
    const isGreatPay = assignment.great_pay;
    const distance = assignment.less_than_300_miles_away;
    const isFirstToApply = assignment.first_to_apply;

    const badges = filterBadges([
      hasBonus && { variant: BADGE_TYPE.bonus, value: assignment.one_time_bonus_burden_cents },
      hasMissingRequirements && { variant: BADGE_TYPE.missing_requirements },
      isGreatPay && { variant: BADGE_TYPE.great_pay },
      distance && { variant: BADGE_TYPE.travel_distance },
      isFirstToApply && { variant: BADGE_TYPE.first_to_apply },
    ]);

    return { ...assignment, badges };
  });
